import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabaseClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const productsPerPage = 10;

  // Modal state for notifications
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success', 'error', 'confirm'
    message: '',
    onConfirm: null
  });

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    quantity: '',
    price: '',
    image: ''
  });

  const [previewImage, setPreviewImage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // Cloudinary config
  const CLOUDINARY_CLOUD_NAME = 'dvpykje0h';
  const CLOUDINARY_UPLOAD_PRESET = 'ml_default';
  const CLOUDINARY_FOLDER = 'products';

  useEffect(() => {
    const authStatus = localStorage.getItem('adminAuth');
    if (authStatus !== 'true') {
      navigate('/admin');
    } else {
      fetchProducts();
    }
  }, [navigate]);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, categoryFilter]);

  const showNotification = (type, message, onConfirm = null) => {
    setNotification({ show: true, type, message, onConfirm });
  };

  const closeNotification = () => {
    setNotification({ show: false, type: '', message: '', onConfirm: null });
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('id', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
      
      const uniqueCategories = [...new Set(data.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showNotification('error', 'La taille du fichier ne doit pas dépasser 10MB');
        return;
      }
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', CLOUDINARY_FOLDER);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw error;
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({ name: '', category: '', quantity: '', price: '', image: '' });
    setPreviewImage('');
    setSelectedFile(null);
    setShowNewCategory(false);
    setNewCategoryName('');
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      quantity: product.quantity.toString(),
      price: product.price.toString(),
      image: product.image
    });
    setPreviewImage(product.image);
    setSelectedFile(null);
    setShowNewCategory(false);
    setNewCategoryName('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name || !formData.quantity || !formData.price) {
      showNotification('error', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Category validation
    const categoryToUse = showNewCategory ? newCategoryName : formData.category;
    if (!categoryToUse) {
      showNotification('error', 'Veuillez sélectionner ou créer une catégorie');
      return;
    }

    // Image validation for new products
    if (!editingProduct && !selectedFile) {
      showNotification('error', 'Veuillez sélectionner une image');
      return;
    }

    try {
      setUploading(true);
      let imageUrl = formData.image;

      // Upload image if a new file is selected
      if (selectedFile) {
        imageUrl = await uploadToCloudinary(selectedFile);
      }

      const productData = {
        name: formData.name,
        category: categoryToUse,
        quantity: parseInt(formData.quantity),
        price: parseFloat(formData.price),
        image: imageUrl
      };

      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
        showNotification('success', 'Produit modifié avec succès !');
      } else {
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
        showNotification('success', 'Produit ajouté avec succès !');
      }

      setShowModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de l\'enregistrement du produit');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id) => {
    showNotification('confirm', 'Êtes-vous sûr de vouloir supprimer ce produit ?', async () => {
      try {
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', id);

        if (error) throw error;
        showNotification('success', 'Produit supprimé avec succès !');
        fetchProducts();
      } catch (error) {
        console.error('Erreur:', error);
        showNotification('error', 'Erreur lors de la suppression du produit');
      }
    });
  };

  const handleLogout = () => {
    showNotification('confirm', 'Voulez-vous vraiment vous déconnecter ?', () => {
      localStorage.removeItem('adminAuth');
      navigate('/admin');
    });
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '1.5rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: '700' }}>
            Administration
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
            Gestion des produits
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: '2px solid #c59b00',
            color: '#c59b00',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.3s',
            fontSize: '0.9rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#c59b00';
            e.target.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#c59b00';
          }}
        >
          Se déconnecter
        </button>
      </div>

      {/* Main Content */}
      <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Filters and Actions */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: '1 1 200px',
                padding: '0.75rem 1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c59b00'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer',
                background: 'white',
                minWidth: '150px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c59b00'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            >
              <option value="all">Toutes catégories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button
              onClick={openAddModal}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 2px 10px rgba(197, 155, 0, 0.3)',
                transition: 'all 0.3s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Nouveau Produit
            </button>
          </div>
        </div>

        {/* Products Display */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            Chargement...
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              display: window.innerWidth > 768 ? 'block' : 'none'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9f9f9', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Image</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Nom</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Catégorie</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Quantité</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Prix</th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#333' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((product) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem' }}>
                        <img
                          src={product.image}
                          alt={product.name}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid #f0f0f0'
                          }}
                          onError={(e) => e.target.src = 'https://via.placeholder.com/60?text=Image'}
                        />
                      </td>
                      <td style={{ padding: '1rem', fontWeight: '500' }}>{product.name}</td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: '#f0f0f0',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          color: '#666'
                        }}>
                          {product.category}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: product.quantity > 10 ? '#e8f5e9' : '#ffebee',
                          color: product.quantity > 10 ? '#2e7d32' : '#c62828',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {product.quantity}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', fontWeight: '600', color: '#c59b00' }}>
                        {product.price.toFixed(2)} €
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={() => openEditModal(product)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#c59b00',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '0.5rem',
                            fontSize: '0.85rem',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#d3c48eff'}
                          onMouseLeave={(e) => e.target.style.background = '#c59b00'}
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#d32f2f'}
                          onMouseLeave={(e) => e.target.style.background = '#f44336'}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div style={{
              display: window.innerWidth <= 768 ? 'block' : 'none'
            }}>
              {currentProducts.map((product) => (
                <div key={product.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <img
                      src={product.image}
                      alt={product.name}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #f0f0f0'
                      }}
                      onError={(e) => e.target.src = 'https://via.placeholder.com/80?text=Image'}
                    />
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>{product.name}</h3>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: '#f0f0f0',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        color: '#666'
                      }}>
                        {product.category}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Quantité</div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: product.quantity > 10 ? '#e8f5e9' : '#ffebee',
                        color: product.quantity > 10 ? '#2e7d32' : '#c62828',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        {product.quantity}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Prix</div>
                      <div style={{ fontWeight: '700', color: '#c59b00', fontSize: '1.2rem' }}>
                        {product.price.toFixed(2)} €
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => openEditModal(product)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#c59b00',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '1.5rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? '#e0e0e0' : '#000',
                    color: currentPage === 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ← Précédent
                </button>
                <span style={{ padding: '0 1rem', color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === totalPages ? '#e0e0e0' : '#000',
                    color: currentPage === totalPages ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          overflowY: 'auto'
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            margin: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '2px solid #f0f0f0',
              background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
              color: 'white',
              borderRadius: '16px 16px 0 0'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
                {editingProduct ? '✏️ Modifier le produit' : '✨ Nouveau produit'}
              </h2>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* Nom */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Nom du produit *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#c59b00'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Catégorie */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Catégorie *
                </label>
                
                {!showNewCategory ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        outline: 'none',
                        cursor: 'pointer',
                        background: 'white'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#c59b00'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowNewCategory(true)}
                      style={{
                        padding: '0.75rem 1rem',
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Nouvelle
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nom de la nouvelle catégorie"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '0.5rem'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#c59b00'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    />
                    <button
                      onClick={() => {
                        setShowNewCategory(false);
                        setNewCategoryName('');
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ← Retour aux catégories existantes
                    </button>
                  </div>
                )}
              </div>

              {/* Quantité et Prix */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Quantité *
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#c59b00'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Prix (€) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#c59b00'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Image du produit *
                </label>
                <div style={{
                  border: '2px dashed #e0e0e0',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  background: '#fafafa',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#c59b00'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                    {!previewImage ? (
                      <div>
                        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📷</div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                          Cliquez pour choisir une image
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          PNG, JPG jusqu'à 10MB
                        </div>
                      </div>
                    ) : (
                      <div>
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            marginBottom: '0.5rem'
                          }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200?text=Erreur';
                          }}
                        />
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          Cliquez pour changer l'image
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '2rem'
              }}>
                <button
                  onClick={() => setShowModal(false)}
                  disabled={uploading}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: '#e0e0e0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: uploading ? '#999' : 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    boxShadow: uploading ? 'none' : '0 2px 10px rgba(197, 155, 0, 0.3)'
                  }}
                >
                  {uploading ? '⏳ Upload en cours...' : (editingProduct ? 'Modifier' : 'Ajouter')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }} onClick={notification.type === 'confirm' ? undefined : closeNotification}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '1.5rem',
              textAlign: 'center',
              borderBottom: notification.type === 'confirm' ? '1px solid #f0f0f0' : 'none'
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                {notification.type === 'success' && '✅'}
                {notification.type === 'error' && '❌'}
                {notification.type === 'confirm' && '⚠️'}
              </div>
              <p style={{
                margin: 0,
                fontSize: '1.1rem',
                color: '#333',
                lineHeight: '1.5'
              }}>
                {notification.message}
              </p>
            </div>

            {notification.type === 'confirm' ? (
              <div style={{
                display: 'flex',
                gap: '0',
                borderTop: '1px solid #f0f0f0'
              }}>
                <button
                  onClick={closeNotification}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'white',
                    color: '#666',
                    border: 'none',
                    borderRight: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (notification.onConfirm) {
                      notification.onConfirm();
                    }
                    closeNotification();
                  }}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'white',
                    color: '#c59b00',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  Confirmer
                </button>
              </div>
            ) : (
              <div style={{ padding: '1rem' }}>
                <button
                  onClick={closeNotification}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: notification.type === 'success' ? '#4caf50' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;